import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import api from "../../services/api";
import { useAuth } from "../../auth/AuthContext";
import styles from "./ChatWidget.module.css";

// ─── helpers ────────────────────────────────────────────────────────────────

function generateMessageId() {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

function formatTime(date) {
  return date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function getPlanTier(user) {
  const plan = user?.tenant?.plan;
  if (!plan) return null;
  const aiType = (plan.aiType || "").toUpperCase();
  if (aiType === "ADVANCED" || aiType === "ENTERPRISE") return "ADVANCED";
  if (aiType === "BASIC") return "BASIC";
  return null;
}

function hasAiIncluded(user) {
  return user?.tenant?.plan?.aiIncluded !== false;
}

// ─── Tab constants ───────────────────────────────────────────────────────────

const TABS = [
  { id: "chat", label: "Chat" },
  { id: "health", label: "Saúde" },
  { id: "actions", label: "Ações" },
];

// ─── Typing dots component ───────────────────────────────────────────────────

function TypingDots() {
  return (
    <div className={styles.typingDots}>
      <span />
      <span />
      <span />
    </div>
  );
}

// ─── Gauge component (circular progress) ────────────────────────────────────

function ScoreGauge({ score }) {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  const color =
    score >= 75 ? "#22c55e" : score >= 50 ? "#f59e0b" : "#ef4444";

  return (
    <div className={styles.gaugeWrapper}>
      <svg width="100" height="100" viewBox="0 0 100 100">
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="10"
        />
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 50 50)"
          style={{ transition: "stroke-dashoffset 0.8s ease" }}
        />
      </svg>
      <div className={styles.gaugeLabel} style={{ color }}>
        {score}%
      </div>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function ChatWidget({ user: propUser, customer: _customer }) {
  const { user: authUser } = useAuth();
  const user = authUser || propUser;

  // Widget state
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("chat");
  const [unreadCount, setUnreadCount] = useState(0);

  // Refs to avoid stale closures in async callbacks
  const openRef = useRef(open);
  const tabRef = useRef(tab);
  useEffect(() => { openRef.current = open; }, [open]);
  useEffect(() => { tabRef.current = tab; }, [tab]);

  // Chat mode
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Health mode
  const [healthData, setHealthData] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState(null);
  const healthFetchedRef = useRef(false);

  // Resolve tenantId: AuthContext user → localStorage["user"] (authService) → null
  const tenantId = user?.tenant?.id || user?.tenantId || (() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}").tenantId || null; }
    catch { return null; }
  })();
  const prevTenantIdRef = useRef(tenantId);
  useEffect(() => {
    if (prevTenantIdRef.current !== tenantId) {
      healthFetchedRef.current = false;
      setHealthData(null);
      setHealthError(null);
      prevTenantIdRef.current = tenantId;
    }
  }, [tenantId]);

  // Actions mode
  const [actionInput, setActionInput] = useState("");
  const [actionResult, setActionResult] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [activeAction, setActiveAction] = useState(null);

  const planTier = getPlanTier(user);
  const aiEnabled = hasAiIncluded(user);

  // ── auto-scroll on new messages ──────────────────────────────────────────
  useEffect(() => {
    if (tab === "chat") {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, chatLoading, tab]);

  // ── focus input when chat opens ──────────────────────────────────────────
  useEffect(() => {
    if (open && tab === "chat") {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, tab]);

  // ── clear unread when widget opens on chat tab ───────────────────────────
  useEffect(() => {
    if (open && tab === "chat") {
      setUnreadCount(0);
    }
  }, [open, tab]);

  // ── focus trap: keep Tab key inside the dialog when open ─────────────────
  const panelRef = useRef(null);
  useEffect(() => {
    if (!open) return;

    const FOCUSABLE =
      'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function handleKeyDown(e) {
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  // ── toggle ───────────────────────────────────────────────────────────────
  const toggle = useCallback(() => {
    setOpen((v) => !v);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // CHAT
  // ─────────────────────────────────────────────────────────────────────────

  const onSend = useCallback(async () => {
    const text = input.trim();
    if (!text || chatLoading) return;

    if (!tenantId) {
      console.warn("ChatWidget: tenantId not available, aborting send.");
      return;
    }

    const ts = new Date();

    // Optimistic: add user message immediately
    setMessages((prev) => [
      ...prev,
      { id: generateMessageId(), role: "user", content: text, time: ts },
    ]);
    setInput("");
    setChatLoading(true);

    try {
      const { data } = await api.post("/ai/nex-chat", {
        message: text,
        history: messages.map((m) => ({ role: m.role, content: m.content })),
      });

      const reply = data.reply || data.message || "";
      const replyTs = new Date();

      setMessages((prev) => [
        ...prev,
        { id: generateMessageId(), role: "assistant", content: reply, time: replyTs },
      ]);

      // Use refs to read current open/tab state — avoids stale closure
      if (!openRef.current || tabRef.current !== "chat") {
        setUnreadCount((n) => n + 1);
      }
    } catch (err) {
      console.error("ChatWidget sendToAI error:", err);
      setMessages((prev) => [
        ...prev,
        {
          id: generateMessageId(),
          role: "assistant",
          content: "Desculpe, ocorreu um erro. Tente novamente.",
          time: new Date(),
        },
      ]);
    } finally {
      setChatLoading(false);
    }
  }, [input, chatLoading, tenantId, messages]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        onSend();
      }
    },
    [onSend]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // HEALTH
  // ─────────────────────────────────────────────────────────────────────────

  const fetchHealth = useCallback(async () => {
    if (healthFetchedRef.current) return;
    setHealthLoading(true);
    setHealthError(null);
    try {
      const { data } = await api.get("/ai/account-health");
      setHealthData(data);
      healthFetchedRef.current = true;
    } catch (err) {
      console.error("ChatWidget health error:", err);
      setHealthError("Não foi possível carregar a análise. Tente novamente.");
    } finally {
      setHealthLoading(false);
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // ACTIONS
  // ─────────────────────────────────────────────────────────────────────────

  const runAction = useCallback(
    async (actionId, prompt) => {
      setActionLoading(true);
      setActionError(null);
      setActionResult(null);
      setActiveAction(actionId);

      try {
        let data;

        if (actionId === "suggest-reply") {
          const res = await api.post("/ai/suggest-reply", {
            message: prompt,
          });
          data = res.data;
        } else if (actionId === "follow-up") {
          const res = await api.post("/ai/follow-up");
          data = res.data;
        } else if (actionId === "scheduling-gaps") {
          const res = await api.get("/ai/scheduling-gaps");
          data = res.data;
        }

        setActionResult({ actionId, data });
      } catch (err) {
        console.error("ChatWidget action error:", err);
        setActionError("Erro ao executar ação. Tente novamente.");
      } finally {
        setActionLoading(false);
      }
    },
    []
  );

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER helpers
  // ─────────────────────────────────────────────────────────────────────────

  function renderChatTab() {
    return (
      <>
        <div className={styles.body} role="log" aria-live="polite">
          {messages.length === 0 && (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>🤖</div>
              <p>Olá! Sou a Nex, sua assistente Nexus Tech. Como posso ajudar?</p>
            </div>
          )}
          {messages.map((m) => (
            <div
              key={m.id}
              className={m.role === "user" ? styles.msgUser : styles.msgBot}
            >
              <div className={styles.msgContent}>{m.content}</div>
              {m.time && (
                <div className={styles.msgTime}>{formatTime(m.time)}</div>
              )}
            </div>
          ))}
          {chatLoading && (
            <div className={styles.msgBot}>
              <TypingDots />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className={styles.inputRow}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Pergunte… (Enter envia, Shift+Enter quebra linha)"
            aria-label="Mensagem para a IA"
            rows={1}
            className={styles.textarea}
          />
          <button
            onClick={onSend}
            disabled={chatLoading || !input.trim()}
            type="button"
            className={styles.sendBtn}
            aria-label="Enviar mensagem"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
        </div>
      </>
    );
  }

  function renderHealthTab() {
    if (healthLoading) {
      return (
        <div className={styles.centerState}>
          <TypingDots />
          <p className={styles.centerLabel}>Analisando sua conta…</p>
        </div>
      );
    }

    if (healthError) {
      return (
        <div className={styles.centerState}>
          <p className={styles.errorText}>{healthError}</p>
          <button
            className={styles.analyzeBtn}
            onClick={() => {
              healthFetchedRef.current = false;
              fetchHealth();
            }}
          >
            Tentar novamente
          </button>
        </div>
      );
    }

    if (!healthData) {
      return (
        <div className={styles.centerState}>
          <div className={styles.emptyIcon}>📊</div>
          <p className={styles.centerLabel}>
            Clique em Analisar para ver insights sobre seu negócio
          </p>
          <button className={styles.analyzeBtn} onClick={fetchHealth}>
            Analisar agora
          </button>
        </div>
      );
    }

    const { score = 0, breakdown = {}, insights = [] } = healthData;

    return (
      <div className={styles.healthBody}>
        <ScoreGauge score={score} />

        {Object.keys(breakdown).length > 0 && (
          <div className={styles.breakdown}>
            <h4 className={styles.sectionTitle}>Detalhamento</h4>
            {Object.entries(breakdown).map(([key, val]) => (
              <div key={key} className={styles.breakdownItem}>
                <div className={styles.breakdownLabel}>
                  <span>{key}</span>
                  <span>{val}%</span>
                </div>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${val}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {insights.length > 0 && (
          <div className={styles.insights}>
            <h4 className={styles.sectionTitle}>Insights</h4>
            <ol className={styles.insightList}>
              {insights.map((ins, i) => (
                <li key={i} className={styles.insightItem}>
                  {ins}
                </li>
              ))}
            </ol>
          </div>
        )}

        <button
          className={styles.analyzeBtn}
          style={{ marginTop: "0.75rem" }}
          onClick={() => {
            healthFetchedRef.current = false;
            setHealthData(null);
            fetchHealth();
          }}
        >
          Atualizar análise
        </button>
      </div>
    );
  }

  function renderActionResult() {
    if (!actionResult) return null;
    const { actionId, data } = actionResult;

    if (actionId === "suggest-reply") {
      const suggestions = data.suggestions || data.replies || [];
      return (
        <div className={styles.actionResult}>
          <p className={styles.actionResultLabel}>Sugestões de resposta:</p>
          <div className={styles.chipGrid}>
            {suggestions.map((s, i) => (
              <button
                key={i}
                className={styles.chip}
                onClick={() => {
                  setTab("chat");
                  setInput(typeof s === "string" ? s : s.text || "");
                  setTimeout(() => inputRef.current?.focus(), 50);
                }}
              >
                {typeof s === "string" ? s : s.text}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (actionId === "follow-up") {
      const list = data.followUps || data.customers || [];
      return (
        <div className={styles.actionResult}>
          <p className={styles.actionResultLabel}>Clientes para follow-up:</p>
          <ul className={styles.actionList}>
            {list.map((item, i) => (
              <li key={i} className={styles.actionListItem}>
                <strong>{item.customerId}</strong>
                <span>{item.message}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    }

    if (actionId === "scheduling-gaps") {
      const gaps = data.gaps || data.slots || [];
      return (
        <div className={styles.actionResult}>
          <p className={styles.actionResultLabel}>Horários disponíveis:</p>
          <ul className={styles.actionList}>
            {gaps.map((g, i) => (
              <li key={i} className={styles.actionListItem}>
                {typeof g === "string" ? g : JSON.stringify(g)}
              </li>
            ))}
          </ul>
        </div>
      );
    }

    return null;
  }

  function renderActionsTab() {
    const isAdvanced = planTier === "ADVANCED";

    const ACTIONS = [
      {
        id: "suggest-reply",
        icon: "💬",
        label: "Sugerir Respostas",
        tier: "BASIC",
        needsInput: true,
        placeholder: "Qual mensagem você quer responder?",
      },
      {
        id: "follow-up",
        icon: "📞",
        label: "Follow-up Clientes",
        tier: "ADVANCED",
        needsInput: false,
      },
      {
        id: "scheduling-gaps",
        icon: "📅",
        label: "Analisar Horários",
        tier: "ADVANCED",
        needsInput: false,
      },
    ];

    const available = ACTIONS.filter(
      (a) => a.tier === "BASIC" || isAdvanced
    );
    const locked = ACTIONS.filter(
      (a) => a.tier !== "BASIC" && !isAdvanced
    );

    const selectedAction = ACTIONS.find((a) => a.id === activeAction);

    return (
      <div className={styles.actionsBody}>
        <div className={styles.actionGrid}>
          {available.map((action) => (
            <button
              key={action.id}
              className={`${styles.actionCard} ${
                activeAction === action.id ? styles.actionCardActive : ""
              }`}
              onClick={() => {
                setActiveAction(action.id);
                setActionResult(null);
                setActionError(null);
                setActionInput("");
                if (!action.needsInput) {
                  runAction(action.id);
                }
              }}
              disabled={actionLoading}
            >
              <span className={styles.actionIcon}>{action.icon}</span>
              <span className={styles.actionLabel}>{action.label}</span>
            </button>
          ))}

          {locked.map((action) => (
            <div key={action.id} className={`${styles.actionCard} ${styles.actionCardLocked}`}>
              <span className={styles.actionIcon}>{action.icon}</span>
              <span className={styles.actionLabel}>{action.label}</span>
              <span className={styles.lockBadge}>ADVANCED</span>
            </div>
          ))}
        </div>

        {selectedAction?.needsInput && !actionResult && (
          <div className={styles.actionInputArea}>
            <textarea
              className={styles.textarea}
              value={actionInput}
              onChange={(e) => setActionInput(e.target.value)}
              placeholder={selectedAction.placeholder}
              rows={2}
            />
            <button
              className={styles.analyzeBtn}
              disabled={actionLoading || !actionInput.trim()}
              onClick={() => runAction(activeAction, actionInput)}
            >
              {actionLoading ? "Gerando…" : "Gerar sugestões"}
            </button>
          </div>
        )}

        {actionLoading && (
          <div className={styles.centerState}>
            <TypingDots />
          </div>
        )}

        {actionError && (
          <p className={styles.errorText}>{actionError}</p>
        )}

        {renderActionResult()}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // MAIN RENDER
  // ─────────────────────────────────────────────────────────────────────────

  const panel = (
    <div className={styles.container} style={{ zIndex: 99999 }}>
      {/* FAB */}
      <button
        className={styles.fab}
        onClick={toggle}
        aria-label={open ? "Fechar chat" : "Abrir chat Nex"}
        type="button"
      >
        {open ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
          </svg>
        )}
        {!open && unreadCount > 0 && (
          <span className={styles.badge}>{unreadCount > 9 ? "9+" : unreadCount}</span>
        )}
      </button>

      {/* Chat panel */}
      <div
        ref={panelRef}
        className={`${styles.chat} ${open ? styles.chatOpen : ""}`}
        role="dialog"
        aria-label="Nex"
        aria-modal="true"
        aria-hidden={!open}
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.avatar}>N</div>
            <div className={styles.headerInfo}>
              <span className={styles.headerName}>Nex</span>
              <span className={styles.onlineIndicator}>
                <span className={styles.onlineDot} />
                online
              </span>
            </div>
          </div>
          <button
            className={styles.closeBtn}
            onClick={toggle}
            aria-label="Fechar"
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        {/* Upgrade banner */}
        {!aiEnabled && (
          <div className={styles.upgradeBanner}>
            <span>Upgrade para usar IA</span>
            <a href="/subscription/plans" className={styles.upgradeLink}>
              Ver planos →
            </a>
          </div>
        )}

        {/* Tabs */}
        <div className={styles.tabs} role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              className={`${styles.tabBtn} ${tab === t.id ? styles.tabBtnActive : ""}`}
              onClick={() => setTab(t.id)}
              type="button"
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className={styles.tabContent}>
          {tab === "chat" && renderChatTab()}
          {tab === "health" && renderHealthTab()}
          {tab === "actions" && renderActionsTab()}
        </div>
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}
